import i18next, { type Resource } from 'i18next'
import LanguageDetector from 'i18next-browser-languagedetector'
import { initReactI18next } from 'react-i18next'
import { ControlLanguageModule } from '#i18n/ControlLanguageModule'
import { type DynamicResource, LazyResourceBackend } from '#i18n/LazyResourceBackend'
import { Pseudo } from './PseudoTranslation'

export const availableLanguages = ['en', 'de', 'it', 'dev', 'pseudo', 'only-keys'] as const

export const isDebugI18n = import.meta.env.MODE === 'development'
export const verbose = import.meta.env.VITE_I18N_VERBOSE === 'true'

export type ResourceConfig =
  | {
      kind: 'static'
      resources: Resource
    }
  | {
      kind: 'dynamic'
      resources: DynamicResource
    }

const makeI18nInternal = async (namespaces: readonly string[], resourceConfig: ResourceConfig) => {
  let instance = i18next.use(new ControlLanguageModule()).use(LanguageDetector)

  if (resourceConfig.kind === 'dynamic') {
    instance = instance.use(new LazyResourceBackend())
  }
  instance = instance.use(initReactI18next).use(new Pseudo())

  try {
    await instance.init({
      debug: isDebugI18n && verbose,
      // do not use fallback language in dev mode
      fallbackLng: isDebugI18n ? undefined : 'en',
      returnEmptyString: false,
      interpolation: {
        // not needed for react as it escapes by default
        escapeValue: false,
      },
      backend: {
        resources: resourceConfig.kind === 'dynamic' ? resourceConfig.resources : undefined,
      } satisfies {
        resources?: DynamicResource
      },
      supportedLngs: availableLanguages,
      // in dev mode do not use default values
      parseMissingKeyHandler: isDebugI18n
        ? (key, defaultValue) => {
            return instance.language === 'pseudo' ? defaultValue : `${instance.language}: ${key}`
          }
        : undefined,
      i18nFormat: {
        bindI18nStore: 'added',
      },
      react: {
        bindI18nStore: 'added',
      },
      postProcess: isDebugI18n ? ['pseudo'] : undefined,
      ns: namespaces,
      resources: resourceConfig.kind === 'static' ? resourceConfig.resources : undefined,
      defaultNS: namespaces,
    })
  } catch (e) {
    console.error(e)
  }

  return instance
}

export const makeI18n = (() => {
  let instance: Awaited<ReturnType<typeof makeI18nInternal>> | undefined

  return async (namespaces: readonly string[], resources: ResourceConfig) => {
    if (!instance) {
      instance = await makeI18nInternal(namespaces, resources)
    }
    return instance
  }
})()
